/**
 * Journey (Mediavine) ad tag loader + the premium ad-free hint it reads.
 *
 * WHY A HINT AND NOT THE REAL SUBSCRIPTION: premium status resolves from
 * `/api/user/subscription`, which AuthContext only calls after
 * `supabase.auth.getSession()` settles. That is hundreds of milliseconds after
 * the Journey wrapper has loaded from <head> and scanned the DOM, so the real
 * answer always arrives too late to decide anything. There is also no
 * server-side fallback: the site has no middleware, and blog/guides/tools are
 * statically generated, so their HTML is byte-identical for every visitor.
 *
 * So we mirror the subscription into localStorage, exactly as
 * `auth_skeleton_hint` already mirrors login state for LoginButton, and read it
 * from a synchronous inline script in <head>. Synchronous beats the async
 * wrapper by construction — we simply never inject the tag.
 *
 * A free reader can of course set this key by hand. That is not a threat model
 * worth defending: any ad blocker does the same job better, and the downside is
 * one lost impression.
 */

/** localStorage key read by JOURNEY_TAG_LOADER before it injects the tag. */
export const PREMIUM_ADS_HINT_KEY = 'wgis_ads'

/** The only value that suppresses the tag. Any other value, or none, serves. */
export const PREMIUM_ADS_HINT_OFF = 'off'

/**
 * Kept VERBATIM from the Journey dashboard, including the protocol-relative
 * form, so Mediavine's install verifier can string-match it in our served HTML.
 * It is interpolated into the loader below as one contiguous literal for the
 * same reason — never split or concatenate it.
 */
export const JOURNEY_TAG_SRC =
  '//scripts.scriptwrapper.com/tags/d718fcea-7a80-41e3-86e6-ad1dbbcc7ab5.js'

/**
 * Inline <head> script that injects the Journey tag for everyone except a
 * signed-in premium reader. Mirrors the attributes of the static tag it
 * replaced (`async`, `data-noptimize`, `data-cfasync`) and the inject-from-head
 * shape the Grow tag above it already uses, which has run in production since
 * the 2026-07-30 launch without a hydration problem.
 *
 * The try/catch matters: localStorage throws outright in some private-browsing
 * modes, and a throw here would cost every such reader their ads.
 */
/**
 * ONE template literal, deliberately. Do NOT split this back into `+`-joined
 * pieces.
 *
 * This was authored as four concatenated template literals and Turbopack's
 * minifier (Next 16.2.6) folded them into a single string constant while
 * silently DROPPING the two middle pieces, emitting:
 *
 *   !(function(){try{if(...getItem("wgis_ads")==="offs.src="//scripts...
 *
 * — a syntax error, so the tag never injected and the site served zero ads on
 * every route until it was caught in production. The corruption is in the built
 * server chunk, not in React's rendering, and `journey-tag.test.ts` cannot see
 * it: Jest reads this module unminified, so the test asserts the source string
 * and passes while the shipped bundle is broken. Verify changes here against
 * `.next/server/chunks` or the served HTML, never against the unit test alone.
 */
export const JOURNEY_TAG_LOADER = `!(function(){try{if(window.localStorage.getItem("${PREMIUM_ADS_HINT_KEY}")==="${PREMIUM_ADS_HINT_OFF}")return}catch(e){}var s=document.createElement("script");s.type="text/javascript";s.async=!0;s.setAttribute("data-noptimize","1");s.setAttribute("data-cfasync","false");s.src="${JOURNEY_TAG_SRC}";document.head.appendChild(s);})();`

/**
 * Mirror subscription state into the hint. Call this wherever subscription
 * state is set, including the null cases — a stale `off` on a lapsed or
 * signed-out account is a silent revenue leak, and it is the failure mode that
 * would never show up in testing because it looks like a working ad-free tier.
 *
 * Takes effect on the reader's next full page load. An App Router client
 * navigation does not re-run <head>, so the wrapper loaded for a session
 * survives until then. See the feature note for that gap.
 */
export function setPremiumAdHint(isPremium: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (isPremium) {
      window.localStorage.setItem(PREMIUM_ADS_HINT_KEY, PREMIUM_ADS_HINT_OFF)
    } else {
      window.localStorage.removeItem(PREMIUM_ADS_HINT_KEY)
    }
  } catch {
    // localStorage may be blocked; the reader just keeps seeing ads.
  }
}
